import setDayOfYear from 'date-fns/setDayOfYear'
import startOfDay from 'date-fns/startOfDay'
// import format from 'date-fns/format'
/**
 * Class representing data stored on a standard boarding pass
 * If you want the low down see here:
 * https://www.iata.org/contentassets/1dccc9ed041b4f3bbdcf8ee8682e75c4/2021_03_02-bcbp-implementation-guide-version-7-.pdf
 * Parsing inspired from
 * https://shooshx.github.io/BoardingBarcode/
 */
export class BoardingPass {
  /**
   * Create barcode data. Note, if the raw string cannot be parsed into a barcode then an exception is
   * thrown
   * @param {String} raw - the raw string encoded in the barcode from which we parse all data from
   * @param {String} firstName - pax first name
   * @param {Number} lastName - pax last name
   * @param {String} bookingReference - pnr/booking ref
   * @param {String} departure - airport origin code
   * @param {String} arrival - airport destination code
   * @param {String} operator - airline operator code
   * @param {String} flightNumber - the flight number
   * @param {Number} doy - the day of year of the boarding pass in julian days
   * @param {String} seatNumber - the pax seat number as string
   * @param {String} checkinSequence - the pax check in number as string
   * @param {Date} date - the date of flight
   */
  constructor (options) {
    // basic checks
    this.raw = options.raw
    if (!this.raw || this.raw.length < 57) throw new Error('Barcode too short')
    this.firstName = options.firstName
    this.lastName = options.lastName
    this.bookingReference = options.bookingReference
    this.departure = options.departure
    this.arrival = options.arrival
    this.operator = options.operator
    this.flightNumber = options.flightNumber
    this.doy = options.doy
    this.seatNumber = options.seatNumber
    this.checkinSequence = options.checkinSequence

    // get date at start of today, then get date in this year with the correct day of year
    // from the boarding pass.
    // finally check we have a date that is at least equal to today, else how did they fly?
    let today = startOfDay(new Date())
    let dayOfyear = this.doy
    // console.log('current doy: ' + dayOfyear)
    let thisYearDate = setDayOfYear(today, dayOfyear)
    let prevYearDate = new Date(thisYearDate)
    prevYearDate.setFullYear(thisYearDate.getFullYear() - 1)
    if (today >= thisYearDate) {
      this.date = thisYearDate
      // console.log('this year date: ' + this.date.toString())
    } else {
      this.date = prevYearDate
      // console.log('prev year date: ' + format(this.date, 'dd/MM/yyyy'))
    }
  }

  /**
   * Convert a raw boarding pass into a structured json object
   * @param {String} raw - the raw data from the card
   * @returns {object} json object if parsed, else null
   *
   */
  // Example:  'M1DOE/JOHN            EXYZ123 ZRHSFOBA 1234 024F035A0001 100'
  static rawToJson (raw) {
    if (!raw || raw.length < 57) return null
    // hack, probably we need to consider many more scenarios
    // it looks M1 means specific format we support, EM1 informs it's electronic
    if (raw.toUpperCase().startsWith('EM1')) {
      // remove first character to keep M1
      raw = raw.slice(1)
    }
    let name = raw.slice(2, 22).replace(' ', '').split('/')
    let firstName = ''
    let lastName = ''
    if (name.length > 1) {
      firstName = name[1].trim()
    }
    lastName = name[0].trim()
    let bookingReference = raw.slice(22, 30).replace(' ', '')

    let departure = raw.slice(30, 33).replace(' ', '')
    let arrival = raw.slice(33, 36).replace(' ', '')
    let operator = raw.slice(36, 38).replace(' ', '')
    let flightNumber = raw.slice(39, 43).replace(' ', '')

    let doy = parseInt(raw.slice(44, 47).replace(' ', ''), 10)
    let seatNumber = raw.slice(48, 52).replace(' ', '')
    let checkinSequence = raw.slice(52, 56).replace(' ', '')
    return new BoardingPass({
      'raw': raw,
      'firstName': firstName,
      'lastName': lastName,
      'bookingReference': bookingReference,
      'departure': departure,
      'arrival': arrival,
      'operator': operator,
      'flightNumber': flightNumber,
      'doy': doy,
      'seatNumber': seatNumber,
      'checkinSequence': checkinSequence
    })
  }

  get firstName () {
    return this._firstName
  }

  set firstName (value) {
    if (value) this._firstName = String(value)
    else this._firstName = null
  }

  toJSON () {
    return {
      firstName: this._firstName
      // todo
    }
  }
}
